import * as Comlink from 'comlink';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { IVoterRecord } from '../components/ImportVoterHelpers';
import type { IColumnMapping } from '../components/ImportVoterHelpers';
import type { IWorkerApi, IWorkerParseResult, IWorkerProgressMessage } from '../types/worker-types';

export interface IWorkerFullResult extends IWorkerParseResult {
	records: IVoterRecord[];
}

const INITIAL_PROGRESS: IWorkerProgressMessage = {
	phase: 'reading',
	percent: 0,
	message: '',
};

export const useVoterWorker = () => {
	const workerRef = useRef<Worker | null>(null);
	const apiRef = useRef<Comlink.Remote<IWorkerApi> | null>(null);
	const [progress, setProgress] = useState<IWorkerProgressMessage>(INITIAL_PROGRESS);
	const [isParsing, setIsParsing] = useState(false);
	const workerErrorRef = useRef<string | null>(null);

	const ensureWorker = useCallback((): Comlink.Remote<IWorkerApi> => {
		if (apiRef.current) return apiRef.current;

		const worker = new Worker(new URL('../workers/voter-parse.worker.ts', import.meta.url), { type: 'module' });
		workerRef.current = worker;
		apiRef.current = Comlink.wrap<IWorkerApi>(worker);

		worker.onerror = (event) => {
			const msg = event.message || 'Worker crashed unexpectedly';
			console.error('[VoterWorker] onerror:', msg);
			workerErrorRef.current = msg;
		};

		worker.onmessageerror = () => {
			console.error('[VoterWorker] onmessageerror: failed to deserialize message');
			workerErrorRef.current = 'Failed to deserialize worker message';
		};

		return apiRef.current;
	}, []);

	useEffect(
		() => () => {
			workerRef.current?.terminate();
			workerRef.current = null;
			apiRef.current = null;
		},
		[]
	);

	const parseFile = useCallback(
		async (buffer: ArrayBuffer, fileName: string, mapping: IColumnMapping, headers: string[]): Promise<IWorkerFullResult> => {
			const api = ensureWorker();

			setIsParsing(true);
			workerErrorRef.current = null;
			setProgress({ phase: 'reading', percent: 0, message: 'Starting...' });

			// Accumulate chunks from the worker
			const allRecords: IVoterRecord[] = [];

			try {
				const result = await api.parseFile(
					buffer,
					fileName,
					mapping,
					headers,
					Comlink.proxy((p: IWorkerProgressMessage) => {
						setProgress(p);
					}),
					Comlink.proxy((chunk: IVoterRecord[]) => {
						for (let i = 0; i < chunk.length; i++) {
							allRecords.push(chunk[i]);
						}
					})
				);

				return {
					...result,
					records: allRecords,
				};
			} catch (err) {
				const workerCrashMsg = workerErrorRef.current;
				const errorMsg = workerCrashMsg
					? `Worker crashed: ${workerCrashMsg}`
					: err instanceof Error
						? err.message
						: 'Worker parse failed';

				console.error('[VoterWorker] parseFile error:', errorMsg, err);

				setProgress({
					phase: 'error',
					percent: 0,
					message: errorMsg,
				});
				throw new Error(errorMsg);
			} finally {
				setIsParsing(false);
			}
		},
		[ensureWorker]
	);

	const resetProgress = useCallback(() => {
		setProgress(INITIAL_PROGRESS);
		setIsParsing(false);
		workerErrorRef.current = null;
	}, []);

	return { parseFile, progress, isParsing, resetProgress };
};
